import { useRef, useState, type FormEvent } from 'react'

import { validateProfessionalEnquiry, type ProfessionalErrors } from '../lib/professionalEnquiry'
import {
  productionProfessionalFormReady,
  recordProfessionalEvent,
  SubmissionError,
  submitProfessionalEnquiry,
} from '../lib/submissionApi'
import { Icon } from './Icon'

type YesNoProps = {
  legend: string
  name: string
  error?: string
  onChange?: (value: string) => void
  detailsId?: string
  detailsVisible?: boolean
}

function YesNo({ legend, name, error, onChange, detailsId, detailsVisible = false }: YesNoProps) {
  return (
    <fieldset className={`choice-field${error ? ' has-error' : ''}`} aria-describedby={error ? `${name}-error` : undefined}>
      <legend>{legend} <span aria-hidden="true">*</span></legend>
      <div className="choice-row">
        {['yes', 'no'].map((value) => (
          <label key={value}>
            <input
              type="radio"
              name={name}
              value={value}
              aria-controls={value === 'yes' ? detailsId : undefined}
              aria-expanded={value === 'yes' && detailsId ? detailsVisible : undefined}
              onChange={() => onChange?.(value)}
            />
            <span>{value === 'yes' ? 'Yes' : 'No'}</span>
          </label>
        ))}
      </div>
      {error && <p className="field-error" id={`${name}-error`}>{error}</p>}
    </fieldset>
  )
}

function InlineError({ field, errors }: { field: keyof ProfessionalErrors; errors: ProfessionalErrors }) {
  const error = errors[field]
  return error ? <p className="field-error" id={`${field}-error`}>{error}</p> : null
}

function focusNamedField(form: HTMLFormElement, field: string | undefined) {
  if (!field) return
  const control = Array.from(form.elements).find((element) => (
    element instanceof HTMLElement && 'name' in element && element.name === field
  ))
  if (control instanceof HTMLElement) control.focus()
}

const analyticsGroups = [
  { outcome: 'contact_details', fields: ['fullName', 'email', 'phone', 'location', 'linkedinUrl'] },
  { outcome: 'professional_profile', fields: ['qualification', 'qualificationOther', 'aboutYou', 'hasUsGaap', 'usGaapDetails', 'remoteExperience', 'remoteExperienceDetails'] },
  { outcome: 'work_preferences', fields: ['willingRemote', 'willingUsHours', 'saEligible'] },
  { outcome: 'cv_and_permissions', fields: ['cv', 'privacyAcknowledged'] },
] as const

const formEnabled = import.meta.env.DEV || (
  import.meta.env.VITE_PROFESSIONAL_FORM_ENABLED === 'true' && productionProfessionalFormReady
)

export function ProfessionalEnquiryForm() {
  const formRef = useRef<HTMLFormElement>(null)
  const summaryRef = useRef<HTMLDivElement>(null)
  const [errors, setErrors] = useState<ProfessionalErrors>({})
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [qualification, setQualification] = useState('')
  const [hasUsGaap, setHasUsGaap] = useState('')
  const [remoteExperience, setRemoteExperience] = useState('')
  const idempotencyKeyRef = useRef<string | undefined>(undefined)
  const startedRef = useRef(false)
  const completedGroupsRef = useRef(new Set<string>())
  const progressMessage = status === 'submitting'
    ? 'Sending your professional enquiry securely.'
    : status === 'error'
      ? 'Your enquiry was not sent. Your answers remain available for another attempt.'
      : ''
  const conditionalMessage = [
    hasUsGaap === 'yes'
      ? 'US GAAP experience details question shown.'
      : hasUsGaap === 'no'
        ? 'US GAAP experience details are not required.'
        : '',
    remoteExperience === 'yes'
      ? 'Remote-work experience details question shown.'
      : remoteExperience === 'no'
        ? 'Remote-work experience details are not required.'
        : '',
  ].filter(Boolean).join(' ')

  const trackStart = () => {
    if (startedRef.current) return
    startedRef.current = true
    recordProfessionalEvent({ event: 'enquiry_started' })
  }

  const trackFormActivity = () => {
    trackStart()
    const form = formRef.current
    if (!form) return
    const currentErrors = validateProfessionalEnquiry(new FormData(form))
    for (const group of analyticsGroups) {
      if (completedGroupsRef.current.has(group.outcome)) continue
      if (group.fields.every((field) => !currentErrors[field as keyof ProfessionalErrors])) {
        completedGroupsRef.current.add(group.outcome)
        recordProfessionalEvent({ event: 'form_group_completed', outcome: group.outcome })
      }
    }
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = event.currentTarget
    const formData = new FormData(form)
    const nextErrors = validateProfessionalEnquiry(formData)

    if (Object.keys(nextErrors).length > 0) {
      recordProfessionalEvent({ event: 'submission_failed', outcome: 'validation' })
      setErrors(nextErrors)
      setStatus('idle')
      requestAnimationFrame(() => {
        summaryRef.current?.focus()
        const firstError = Object.keys(nextErrors)[0]
        focusNamedField(form, firstError)
      })
      return
    }

    setErrors({})
    setStatus('submitting')

    try {
      idempotencyKeyRef.current ??= crypto.randomUUID()
      await submitProfessionalEnquiry(formData, idempotencyKeyRef.current)

      form.reset()
      idempotencyKeyRef.current = undefined
      startedRef.current = false
      completedGroupsRef.current.clear()
      setQualification('')
      setHasUsGaap('')
      setRemoteExperience('')
      setStatus('success')
      recordProfessionalEvent({ event: 'submission_succeeded' })
    } catch (error) {
      if (error instanceof SubmissionError && error.fields && Object.keys(error.fields).length > 0) {
        setErrors(error.fields as ProfessionalErrors)
        setStatus('idle')
        requestAnimationFrame(() => {
          summaryRef.current?.focus()
          const firstError = Object.keys(error.fields ?? {})[0]
          focusNamedField(form, firstError)
        })
      } else {
        setStatus('error')
      }
      const outcome = error instanceof SubmissionError && error.status === 401
        ? 'app_check'
        : error instanceof SubmissionError && error.status === 429
          ? 'rate_limited'
          : 'server'
      recordProfessionalEvent({ event: 'submission_failed', outcome })
    }
  }

  if (status === 'success') {
    return (
      <div className="professional-confirmation" role="status" tabIndex={-1}>
        <span className="confirmation-icon"><Icon name="shield" /></span>
        <h3>Enquiry received.</h3>
        <p>Thank you. An acknowledgement will be sent to your email address and we will review your information.</p>
        <ol>
          <li>Ark reviews your professional profile.</li>
          <li>If there may be a match, we contact you by email and phone.</li>
          <li>We ask permission before sharing identifiable information with a client.</li>
        </ol>
        <p className="confirmation-note">No action is needed right now.</p>
        <button className="text-button" type="button" onClick={() => setStatus('idle')}>Send another enquiry</button>
      </div>
    )
  }

  return (
    <form className="professional-form" ref={formRef} noValidate onSubmit={handleSubmit} onInput={trackFormActivity} onChange={trackFormActivity} onBlur={trackFormActivity}>
      <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">{progressMessage}</p>
      <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">{conditionalMessage}</p>
      <p hidden>
        <label>Leave this field empty<input name="website" tabIndex={-1} autoComplete="off" /></label>
      </p>
      {Object.keys(errors).length > 0 && (
        <div className="error-summary" ref={summaryRef} role="alert" tabIndex={-1}>
          <strong>Please check the highlighted answers.</strong>
          <p>We found {Object.keys(errors).length} {Object.keys(errors).length === 1 ? 'item' : 'items'} to correct before you submit.</p>
        </div>
      )}

      <section className="form-group" aria-labelledby="contact-details-heading">
        <div className="form-group-heading">
          <span>01</span>
          <div><h3 id="contact-details-heading">Contact details</h3><p>How Ark can identify and reach you.</p></div>
        </div>
        <div className="form-grid">
          <div className="field">
            <label htmlFor="fullName">Full name <span aria-hidden="true">*</span></label>
            <input id="fullName" name="fullName" autoComplete="name" maxLength={120} aria-invalid={Boolean(errors.fullName)} aria-describedby={errors.fullName ? 'fullName-error' : undefined} />
            <InlineError field="fullName" errors={errors} />
          </div>
          <div className="field">
            <label htmlFor="professional-email">Personal email <span aria-hidden="true">*</span></label>
            <input id="professional-email" name="email" type="email" autoComplete="email" placeholder="you@example.com" maxLength={254} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'email-error' : undefined} />
            <InlineError field="email" errors={errors} />
          </div>
          <div className="field">
            <label htmlFor="phone">Mobile number <span aria-hidden="true">*</span></label>
            <input id="phone" name="phone" type="tel" autoComplete="tel" placeholder="+27" maxLength={40} aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? 'phone-error' : undefined} />
            <InlineError field="phone" errors={errors} />
          </div>
          <div className="field">
            <label htmlFor="location">City or province <span aria-hidden="true">*</span></label>
            <input id="location" name="location" autoComplete="address-level1" placeholder="Cape Town, Western Cape" maxLength={120} aria-invalid={Boolean(errors.location)} aria-describedby={errors.location ? 'location-error' : undefined} />
            <InlineError field="location" errors={errors} />
          </div>
          <div className="field field--wide">
            <label htmlFor="linkedinUrl">LinkedIn profile <span aria-hidden="true">*</span></label>
            <input id="linkedinUrl" name="linkedinUrl" type="url" inputMode="url" autoComplete="url" placeholder="https://www.linkedin.com/in/your-name" maxLength={500} aria-invalid={Boolean(errors.linkedinUrl)} aria-describedby={errors.linkedinUrl ? 'linkedinUrl-error' : 'linkedin-help'} />
            <p className="field-help" id="linkedin-help">A public LinkedIn profile is required; a CV is optional.</p>
            <InlineError field="linkedinUrl" errors={errors} />
          </div>
        </div>
      </section>

      <section className="form-group" aria-labelledby="profile-heading">
        <div className="form-group-heading">
          <span>02</span>
          <div><h3 id="profile-heading">Professional profile</h3><p>Enough context for a thoughtful human review.</p></div>
        </div>
        <div className="form-grid">
          <fieldset className={`choice-field field--wide${errors.qualification ? ' has-error' : ''}`} aria-describedby={errors.qualification ? 'qualification-error' : undefined}>
            <legend>Qualification pathway <span aria-hidden="true">*</span></legend>
            <div className="segmented-choice">
              {([['ca_sa', 'CA(SA)'], ['cima', 'CIMA'], ['other', 'Other']] as const).map(([value, label]) => (
                <label key={value}><input type="radio" name="qualification" value={value} onChange={() => setQualification(value)} /><span>{label}</span></label>
              ))}
            </div>
            <InlineError field="qualification" errors={errors} />
          </fieldset>
          {qualification === 'other' && (
            <div className="field field--wide conditional-field" aria-live="polite">
              <label htmlFor="qualificationOther">Describe your qualification or pathway <span aria-hidden="true">*</span></label>
              <input id="qualificationOther" name="qualificationOther" maxLength={160} aria-invalid={Boolean(errors.qualificationOther)} aria-describedby={errors.qualificationOther ? 'qualificationOther-error' : undefined} />
              <InlineError field="qualificationOther" errors={errors} />
            </div>
          )}
          <div className="field field--wide">
            <label htmlFor="aboutYou">About you <span aria-hidden="true">*</span></label>
            <textarea id="aboutYou" name="aboutYou" rows={6} maxLength={4000} placeholder="Summarise your qualification status, articles or training firm, relevant experience, the opportunity you want, and your availability or notice period." aria-invalid={Boolean(errors.aboutYou)} aria-describedby={errors.aboutYou ? 'aboutYou-error' : 'about-you-help'} />
            <p className="field-help" id="about-you-help">A short, focused summary is perfect.</p>
            <InlineError field="aboutYou" errors={errors} />
          </div>
          <YesNo legend="Do you have US GAAP experience?" name="hasUsGaap" error={errors.hasUsGaap} onChange={setHasUsGaap} detailsId="us-gaap-details" detailsVisible={hasUsGaap === 'yes'} />
          <YesNo legend="Have you worked fully remotely before?" name="remoteExperience" error={errors.remoteExperience} onChange={setRemoteExperience} detailsId="remote-experience-details" detailsVisible={remoteExperience === 'yes'} />
          {hasUsGaap === 'yes' && (
            <div className="field conditional-field" id="us-gaap-details">
              <label htmlFor="usGaapDetails">US GAAP experience <span aria-hidden="true">*</span></label>
              <textarea id="usGaapDetails" name="usGaapDetails" maxLength={1000} placeholder="Briefly describe the work and your level of exposure." aria-invalid={Boolean(errors.usGaapDetails)} aria-describedby={errors.usGaapDetails ? 'usGaapDetails-error' : undefined} />
              <InlineError field="usGaapDetails" errors={errors} />
            </div>
          )}
          {remoteExperience === 'yes' && (
            <div className="field conditional-field" id="remote-experience-details">
              <label htmlFor="remoteExperienceDetails">Remote-work experience <span aria-hidden="true">*</span></label>
              <textarea id="remoteExperienceDetails" name="remoteExperienceDetails" maxLength={1000} placeholder="Briefly describe the team, duration and working model." aria-invalid={Boolean(errors.remoteExperienceDetails)} aria-describedby={errors.remoteExperienceDetails ? 'remoteExperienceDetails-error' : undefined} />
              <InlineError field="remoteExperienceDetails" errors={errors} />
            </div>
          )}
        </div>
      </section>

      <section className="form-group" aria-labelledby="preferences-heading">
        <div className="form-group-heading">
          <span>03</span>
          <div><h3 id="preferences-heading">Work preferences</h3><p>Your answers guide matching; they do not reject you automatically.</p></div>
        </div>
        <div className="form-grid">
          <YesNo legend="Are you willing to work fully remotely?" name="willingRemote" error={errors.willingRemote} />
          <YesNo legend="Are you willing to work US-aligned hours if required?" name="willingUsHours" error={errors.willingUsHours} />
          <YesNo legend="Do you reside in South Africa and may you legally work here?" name="saEligible" error={errors.saEligible} />
        </div>
      </section>

      <section className="form-group form-group--last" aria-labelledby="permissions-heading">
        <div className="form-group-heading">
          <span>04</span>
          <div><h3 id="permissions-heading">CV and permissions</h3><p>Your CV is optional. Your choices stay separate.</p></div>
        </div>
        <div className="form-grid">
          <div className="field field--wide file-field">
            <label htmlFor="cv">Optional CV</label>
            <input id="cv" name="cv" type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" aria-invalid={Boolean(errors.cv)} aria-describedby={errors.cv ? 'cv-error' : 'cv-help'} />
            <p className="field-help" id="cv-help">One PDF, DOC or DOCX file, no larger than 5 MB. Files remain private and are not reviewable until safety checks pass.</p>
            <InlineError field="cv" errors={errors} />
          </div>
          <label className={`check-field field--wide${errors.privacyAcknowledged ? ' has-error' : ''}`}>
            <input type="checkbox" name="privacyAcknowledged" value="yes" aria-invalid={Boolean(errors.privacyAcknowledged)} aria-describedby={errors.privacyAcknowledged ? 'privacyAcknowledged-error' : undefined} />
            <span>I acknowledge that Ark will use my information to review and respond to this enquiry as explained in the <a href="/privacy" target="_blank" rel="noreferrer">privacy notice</a>. <strong>Required</strong></span>
          </label>
          <InlineError field="privacyAcknowledged" errors={errors} />
          <label className="check-field field--wide">
            <input type="checkbox" name="futureOpportunitiesConsent" value="yes" />
            <span>Ark may email me about new positions and contact me if a future opportunity looks suitable. <em>Optional and unchecked by default.</em></span>
          </label>
        </div>
      </section>

      {status === 'error' && (
        <div className="submission-error" role="alert">
          <strong>We could not send your enquiry.</strong>
          <p>Your answers are still here. Please retry or email <a href="mailto:professionals@arksolutionsza.com">professionals@arksolutionsza.com</a>.</p>
        </div>
      )}

      {!formEnabled && (
        <p className="launch-gate-note">Online enquiries are being prepared. Please email professionals@arksolutionsza.com in the meantime.</p>
      )}
      <button className="button button--full professional-submit" type="submit" disabled={!formEnabled || status === 'submitting'}>
        {status === 'submitting' ? 'Sending your enquiry…' : 'Submit enquiry'}
        <Icon name="arrow" />
      </button>
      <p className="form-fine-print">Submitting is an enquiry about possible current or future positions. It is not registration, a job application to a guaranteed vacancy, or an offer of employment.</p>
    </form>
  )
}
